/**
 * Manufacturing & Co-Packing — 5 templates.
 *
 * co-packer-agreement is the flagship document of the library. The other four are the
 * documents that sit around it: the NDA that comes before it, the quality agreement and
 * specification sheet that define "correct product", and the recall agreement that decides
 * who pays when it goes wrong.
 */

export const MANUFACTURING_TEMPLATES = [
  // ───────────────────────────────────────────────────────────────────────────────────────
  // 1. CO-PACKER AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "co-packer-agreement",
    docId: "co-packer-agreement",
    title: "CO-PACKING AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are about to have a third-party manufacturer produce your product commercially, or you are handing over a formula to get a pilot run. Use it before the first production date is booked, not after the first invoice arrives.",
      whyItMatters:
        "Your co-packer controls whether your product physically exists. A weak agreement fails in three predictable ways: you cannot move production without losing your formula, you are holding six figures of ingredients and packaging you never agreed to buy, or a quality failure lands entirely on you because nobody wrote down who pays for a recall. Founders negotiate the per-case price hard and skim everything else, and everything else is where the money is.",
      negotiateCarefully: [
        {
          point: "Formula and improvement ownership",
          detail:
            "Say in one unambiguous sentence that you own the formula, the specifications, and every improvement or modification made to them during production, whoever thought of it. Silence here, or a clause that gives the plant rights in 'improvements', is what stops a founder from taking their own product to a second manufacturer.",
        },
        {
          point: "The binding window on forecasts",
          detail:
            "A rolling forecast is a planning tool right up until it becomes a purchase commitment. Fix exactly how far out the forecast binds you, how much it may flex, and confirm that everything beyond that window is planning only. That one sentence is the difference between a forecast and an order.",
        },
        {
          point: "Excess and obsolete materials, with a cap",
          detail:
            "Materials bought against your forecast become your liability when demand shifts. Cap the exposure, require the plant to try to use, return or resell the material first, and put a deadline on invoicing it — an aged-materials claim that arrives eighteen months later is impossible to verify.",
        },
        {
          point: "How price can move",
          detail:
            "Accept a defined mechanism, not a general right to raise price. Documented pass-through of actual material cost changes, with evidence, advance notice, decreases passed through as well as increases, and a right to walk if you cannot live with the new number.",
        },
        {
          point: "The exit: termination plus transition assistance",
          detail:
            "A termination right with no transition obligation is not an exit. You need the specifications, the tooling, the remaining materials you paid for, the finished goods, and the batch and trace records back within a stated period, and you need it not to be contingent on settling every open invoice dispute first.",
        },
      ],
      redFlags: [
        "Any clause giving the co-packer ownership of, or a licence to, your formula, specifications or improvements.",
        "An unlimited or undefined right to increase price, or price increases with no supporting documentation.",
        "No cap on excess and obsolete materials, and no deadline for invoicing them.",
        "A liability cap set at or below the value of a single production run, with recall costs sitting inside it.",
        "No obligation to return specifications, tooling, materials or finished goods on termination.",
        "A long initial term with automatic renewal and no termination for convenience.",
      ],
      timeToComplete: "60–90 minutes to fill with your operations lead · plan two weeks to negotiate",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[CO-PACKER NAME]", label: "Co-packer's legal name", example: "Midwest Co-Packing, Inc." },
      { token: "[CO-PACKER STATE]", label: "Co-packer's state of formation", example: "Ohio" },
      { token: "[CO-PACKER ENTITY TYPE]", label: "Co-packer's entity type", example: "corporation" },
      { token: "[CO-PACKER ADDRESS]", label: "Co-packer's address", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[FACILITY ADDRESS]", label: "Address of the production facility", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[PRODUCT LIST]", label: "Products covered, by name and size", example: "Acme Sparkling Yuzu, 12 oz can; Acme Sparkling Grapefruit, 12 oz can" },
      { token: "[SPECIFICATION VERSION]", label: "Specification version and date", example: "v1.2 dated August 12, 2026" },
      { token: "[FORECAST FREQUENCY]", label: "How often you submit a forecast", example: "monthly" },
      { token: "[FORECAST HORIZON]", label: "How far the forecast looks ahead", example: "the following twelve months" },
      { token: "[BINDING FORECAST WINDOW]", label: "How much of the forecast is binding", example: "the first 30 days" },
      { token: "[FORECAST FLEX ALLOWANCE]", label: "Permitted swing in the near-term forecast", example: "plus or minus 20%" },
      { token: "[PO LEAD TIME]", label: "Notice required on a purchase order", example: "45 days" },
      { token: "[PO ACCEPTANCE PERIOD]", label: "Time to accept or reject a purchase order", example: "5 business days" },
      { token: "[MINIMUM RUN QUANTITY]", label: "Minimum quantity per production run", example: "1 full truckload per SKU" },
      { token: "[PRICE UNIT]", label: "Unit the tolling price is stated in", example: "per case of 24 cans" },
      { token: "[PRICE REVIEW FREQUENCY]", label: "How often price may be reviewed", example: "once in any 12-month period" },
      { token: "[PRICE INCREASE NOTICE]", label: "Notice required before a price change", example: "60 days" },
      { token: "[PRICE OBJECTION PERIOD]", label: "Your window to object to a price change", example: "15 days" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 30 days from invoice date" },
      { token: "[DEPOSIT AMOUNT]", label: "Deposit, if any, per run", example: "30% of the run value" },
      { token: "[LATE PAYMENT RATE]", label: "Late payment charge", example: "1% per month on undisputed overdue amounts" },
      { token: "[RAW MATERIALS PROCURED BY]", label: "Who buys the ingredients", example: "Co-Packer" },
      { token: "[PACKAGING PROCURED BY]", label: "Who buys the packaging", example: "Brand" },
      { token: "[MATERIALS COMMITMENT WINDOW]", label: "How far ahead materials may be bought", example: "the binding forecast window plus supplier lead time" },
      { token: "[EXCESS MATERIALS CAP]", label: "Cap on your excess and obsolete exposure", example: "the value of one binding forecast period" },
      { token: "[MATERIALS CLAIM DEADLINE]", label: "Deadline to invoice aged or obsolete materials", example: "90 days after the material becomes obsolete" },
      { token: "[VARIANCE ALLOWANCE]", label: "Yield and variance allowance", example: "2% of materials issued per run" },
      { token: "[PRODUCTION LEAD TIME]", label: "Lead time from accepted order to production", example: "30 days" },
      { token: "[SCHEDULE CONFIRMATION PERIOD]", label: "Time to confirm a production date", example: "5 business days" },
      { token: "[FACILITY CERTIFICATIONS]", label: "Certifications the facility must hold", example: "its third-party food safety certification, organic and kosher certifications" },
      { token: "[COA DELIVERY TIMING]", label: "When the certificate of analysis is delivered", example: "before or with each shipment" },
      { token: "[RETAINED SAMPLE PERIOD]", label: "How long retained samples are held", example: "shelf life plus 6 months" },
      { token: "[INSPECTION PERIOD]", label: "Your inspection and rejection window", example: "30 days after receipt" },
      { token: "[REJECTION RESPONSE PERIOD]", label: "Time to respond to a rejection", example: "10 business days" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long records are kept", example: "shelf life plus 2 years" },
      { token: "[AUDIT FREQUENCY]", label: "How often you may audit", example: "once per calendar year" },
      { token: "[AUDIT NOTICE]", label: "Notice required before an audit", example: "10 business days" },
      { token: "[CHANGE NOTICE PERIOD]", label: "Notice required before any change", example: "45 days" },
      { token: "[TRACE RESPONSE TIME]", label: "Time to complete a lot trace on request", example: "4 hours" },
      { token: "[MOCK RECALL FREQUENCY]", label: "How often a mock recall is run", example: "once per calendar year" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$2,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$5,000,000 aggregate" },
      { token: "[RECALL COVERAGE LIMIT]", label: "Recall / contaminated products limit", example: "$1,000,000" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "5" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "2 years" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "1 year" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice to stop automatic renewal", example: "90 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "120 days" },
      { token: "[TRANSITION PERIOD]", label: "Transition assistance period", example: "120 days" },
      { token: "[FORCE MAJEURE PERIOD]", label: "Force majeure duration allowing termination", example: "45 consecutive days" },
      { token: "[LIABILITY CAP]", label: "Liability cap", example: "the amounts paid under this Agreement in the preceding 12 months" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Co-Packing Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS] ("Brand"); and

  [CO-PACKER NAME], a [CO-PACKER STATE] [CO-PACKER ENTITY TYPE], with a principal place of
  business at [CO-PACKER ADDRESS] ("Co-Packer").

Brand sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand. Co-Packer operates a
manufacturing facility with the equipment and personnel to make them. Brand engages Co-Packer to
manufacture and package the Products on the terms below.

1. DEFINITIONS
1.1 "Products" means the finished goods listed in Exhibit A, which as of the Effective Date are
[PRODUCT LIST]. "Facility" means Co-Packer's plant at [FACILITY ADDRESS], and any other location
Brand approves in writing before Product is produced there. "Formula" means, for each Product,
the complete formulation: the identity, grade and source of each ingredient, the quantity of
each, the order and method of addition, and the process parameters that produce the Product.
"Specifications" means the written specification for each Product attached as Exhibit A, version
[SPECIFICATION VERSION], amended only under Section 15. "Materials" means the ingredients,
processing aids, packaging components and shipping materials used to make and pack the Products.
"Lot" means the quantity of a Product produced under a single lot code. "Brand Property" means
the Formula, the Specifications, Brand's trademarks, artwork and label files, Brand-owned
tooling and change parts, Materials owned by Brand, finished Products, and all records and data
generated in connection with the Products.

2. SCOPE OF MANUFACTURING SERVICES
2.1 Co-Packer shall manufacture, package, code, palletise and hold for shipment the Products in
accordance with the Specifications and this Agreement, at the Facility only, using personnel
trained on the Specifications and on its own food safety, sanitation and allergen programs. It
shall not subcontract any part of the work without Brand's prior written consent, and where
consent is given it remains responsible for the subcontractor and shall bind it to obligations
no less protective than Sections 11, 14, 18 and 19.
2.2 Unless the parties agree otherwise in a signed writing, this Agreement is non-exclusive:
Brand may have the Products made elsewhere, and Co-Packer may manufacture for others. Co-Packer
shall use the Formula, the Specifications and Brand Property only to produce Products for Brand,
and for no other purpose or customer.

3. SPECIFICATIONS
3.1 Each Product is defined by its Specification, attached as Exhibit A and signed by both
parties, as is each amendment to it. Co-Packer shall manufacture and package each Product to
conform to its Specification in all respects, including formulation, process parameters,
finished attributes and tolerances, net contents and fill, packaging components, coding, and
case and pallet configuration.
3.2 On any question of what constitutes conforming Product the Specification controls; on
commercial matters this Agreement controls. If the parties enter into a separate quality
agreement, it controls on the technical and quality matters it addresses, and this Agreement
controls on all commercial matters, including liability, remedies and cost.
3.3 Brand is responsible for the content of the label copy and artwork it supplies and for the
accuracy of the claims on it. Co-Packer is responsible for applying the artwork version Brand
has approved in writing and for reproducing the coding and date format the Specification
requires.

4. FORECASTS
4.1 Brand shall give Co-Packer a [FORECAST FREQUENCY] rolling forecast of expected volumes by
Product covering [FORECAST HORIZON]. Co-Packer shall reserve capacity sufficient to meet it and
shall notify Brand promptly if it expects to be unable to do so.
4.2 Only [BINDING FORECAST WINDOW] of each forecast is binding on Brand. All later periods are
planning information given in good faith and create no purchase obligation, no commitment to buy
Materials and no liability of any kind. Within the binding window Brand may vary the forecast
quantity by [FORECAST FLEX ALLOWANCE] at no cost, on written notice given before Materials for
the affected quantity have been ordered.

5. PURCHASE ORDERS AND PRECEDENCE
5.1 Brand shall order by written purchase order stating Product, quantity, price, requested
delivery date and destination, submitted at least [PO LEAD TIME] before the requested production
date. The minimum quantity per run is [MINIMUM RUN QUANTITY] unless the parties agree otherwise
in writing for a particular run. Co-Packer shall accept or reject each order in writing within
[PO ACCEPTANCE PERIOD]; an order consistent with the binding forecast and the agreed
Specifications is deemed accepted if Co-Packer does not respond within that period.
5.2 This Agreement governs every purchase order between the parties. Pre-printed or standard
terms on a purchase order, acknowledgment, invoice, delivery document or portal of either party
have no effect and are rejected, except for the Product, quantity, price, date and destination
stated on the order.

6. PRICING AND PRICE ADJUSTMENT
6.1 The tolling price for each Product is set out in Exhibit B, stated [PRICE UNIT], and
includes labour, line time, overhead, quality control, standard coding and palletising, and
holding of finished goods for the period stated in Exhibit B. Prices are firm and may be
reviewed no more often than [PRICE REVIEW FREQUENCY].
6.2 Co-Packer may adjust price only to pass through a documented change in the cost of Materials
it procures, or in a cost item expressly identified in Exhibit B. A proposed adjustment shall be
supported by supplier invoices, quotations or other written evidence showing the amount of the
change and its effective date, and Co-Packer shall not apply a margin to it. Documented
decreases are passed through on the same basis and in the same period as increases.
6.3 Co-Packer shall give at least [PRICE INCREASE NOTICE] written notice of a proposed increase,
with the supporting documentation. Brand may object within [PRICE OBJECTION PERIOD]. If the
parties do not agree, Brand may terminate on written notice with no liability other than for
accepted orders, Materials properly committed under Section 8, and undisputed invoices.

7. PAYMENT AND DEPOSITS
7.1 Co-Packer shall invoice on shipment of each Lot, referencing the purchase order, Lot code
and quantity. Undisputed invoices are payable [PAYMENT TERMS], and undisputed amounts not paid
when due bear a charge of [LATE PAYMENT RATE]. Where a deposit is agreed for a run it is
[DEPOSIT AMOUNT], is credited against the invoice for that run, and is refundable in full if
Co-Packer fails to produce the run for any reason other than Brand's failure to perform.
7.2 Brand may withhold an amount it disputes in good faith if it pays the undisputed balance and
states the basis in writing. Co-Packer shall not suspend production, withhold shipment, or
assert any lien or other retention right over Brand Property on account of a disputed amount.

8. RAW MATERIALS AND PACKAGING
8.1 Raw materials shall be procured by [RAW MATERIALS PROCURED BY] and packaging by
[PACKAGING PROCURED BY]. Each party shall procure only Materials meeting the Specifications, and
only from suppliers approved in writing. Materials paid for by Brand, and all work in progress
and finished Products, are Brand's property; Co-Packer shall store them separately, identify
them in its records as Brand's, not pledge or encumber them or allow a lien to attach, and bears
risk of loss for Brand Property in its possession.
8.2 Where Co-Packer procures Materials it may purchase against [MATERIALS COMMITMENT WINDOW]
only. Materials bought outside that window, or in excess of the binding forecast, are at
Co-Packer's risk unless Brand approved them in writing in advance.
8.3 Brand is responsible for Materials properly purchased under this Section that become excess
or obsolete, up to a maximum of [EXCESS MATERIALS CAP] in aggregate. Before invoicing Brand,
Co-Packer shall use commercially reasonable efforts to consume them in later production for
Brand, return them to the supplier or resell them, and shall credit Brand with any amount
recovered. Co-Packer shall report on-hand and aged Materials with each production reconciliation
and shall invoice excess or obsolete Materials within [MATERIALS CLAIM DEADLINE], failing which
the claim is waived.
8.4 Labels, cartons and other components bearing Brand's trademarks shall be used only for
Brand's Products and, when obsolete, destroyed with written confirmation to Brand or returned at
Brand's direction.

9. YIELD AND VARIANCE
9.1 The yield and variance allowance is [VARIANCE ALLOWANCE], measured against Materials issued
to each run. Losses above the allowance are at Co-Packer's cost, and Co-Packer shall replace the
affected Materials or credit Brand for them at Brand's landed cost. Co-Packer shall deliver a
written reconciliation for each run showing Materials issued, Product produced, losses and
Materials remaining, and shall investigate and report on any run exceeding the allowance.

10. PRODUCTION SCHEDULING AND LEAD TIMES
10.1 Co-Packer shall schedule accepted orders within [PRODUCTION LEAD TIME] and confirm the
production date within [SCHEDULE CONFIRMATION PERIOD] of accepting the order. It shall notify
Brand promptly of anything likely to delay a confirmed date and propose a revised date. If
Co-Packer fails to produce an accepted order by the confirmed date and does not produce it
within [CURE PERIOD] of that date, Brand may cancel the order without liability and have the
affected quantity produced elsewhere.

11. QUALITY, TESTING AND CERTIFICATES OF ANALYSIS
11.1 Co-Packer shall manufacture, test and release each Lot in accordance with the
Specifications and the testing plan in Exhibit A. The plan shall state, for each test, the
parameter and limit, the method, the sampling point, the frequency, the party performing the
test and the party bearing the cost. A test not assigned in the plan is not being performed, and
neither party may assume otherwise.
11.2 Co-Packer shall deliver a Certificate of Analysis for each Lot [COA DELIVERY TIMING],
stating the Product, the Lot code, the production date, the quantity produced, each parameter
tested with its specification limit, the method used and the actual result, and the name of the
person releasing the Lot. A certificate stating only that the Lot conforms does not satisfy this
Section. Co-Packer shall retain representative samples of each Lot under the storage conditions
in the Specification for [RETAINED SAMPLE PERIOD] and make them available on request.
11.3 Co-Packer shall maintain [FACILITY CERTIFICATIONS] throughout the term and notify Brand
promptly if any lapses, is suspended, is downgraded or is placed under review. It shall also
notify Brand promptly of any deviation, hold, investigation, out-of-specification result,
consumer or customer complaint, or regulatory inspection finding affecting the Products.

12. ACCEPTANCE, REJECTION AND DISPOSITION
12.1 Brand may inspect and test each Lot and may reject Product that does not conform to the
Specifications within [INSPECTION PERIOD], and at any time for a defect not reasonably
discoverable on inspection. Co-Packer shall respond within [REJECTION RESPONSE PERIOD]. If the
parties disagree on conformance they shall submit samples from the Lot to an independent
laboratory they both approve, whose result is final, with the cost borne by the party whose
position is not supported.
12.2 For Product properly rejected because Co-Packer failed to meet the Specifications,
Co-Packer shall at Brand's election replace it at no charge or credit Brand for the invoiced
amount plus Brand's cost of the Materials consumed, and shall bear the cost of freight, storage
and disposal of the rejected Product.
12.3 Non-conforming Product shall be quarantined and segregated, and shall not be reworked,
blended, downgraded, sold, donated or otherwise released without Brand's prior written approval
of the specific disposition. Product bearing Brand's trademarks shall not leave the Facility
except to Brand or at Brand's written direction. Where Product is destroyed, Co-Packer shall
confirm in writing the Lot, quantity and method of destruction.

13. RECORDS AND AUDIT RIGHTS
13.1 Co-Packer shall keep batch records, Materials receiving records and lot traces,
certificates of analysis and test data, sanitation and allergen changeover records, production
reconciliations and shipping records for the Products for [RECORD RETENTION PERIOD], and shall
provide copies to Brand on request.
13.2 Brand and its representatives may audit the Facility and those records [AUDIT FREQUENCY] on
[AUDIT NOTICE] written notice during normal business hours, and on shorter notice following a
quality incident, a recall or a Lot rejection. Co-Packer shall provide, on request, the results
of third-party audits of the Facility and any resulting corrective action plans.

14. FOOD SAFETY AND ALLERGEN CONTROL
14.1 Co-Packer is responsible for operating the Facility, including its own written food safety,
sanitation, pest control, environmental monitoring, maintenance and training programs; for
holding the licences, registrations and permits it needs to manufacture the Products; and for
its compliance with all laws applicable to it as manufacturer. Brand is responsible for the
Formula, for the label copy and artwork it supplies, for the accuracy of the claims made on the
label, and for its compliance with all laws applicable to it as the party placing the Products
on the market.
14.2 Co-Packer shall disclose in writing which allergens are handled at the Facility and on the
lines used for the Products, maintain documented and validated changeover and cleaning
procedures between products containing different allergens, and retain the changeover records.
It shall not introduce a new allergen onto a line used for the Products without notice and
approval under Section 15. The cross-contact status recorded in the Specification describes the
Facility as operated on the date of that Specification.
14.3 Co-Packer shall apply lot coding that permits each finished pallet to be traced back to the
Materials lots consumed and forward to the shipments made, and shall complete a full forward and
backward trace on request within [TRACE RESPONSE TIME].

15. CHANGE CONTROL
15.1 Co-Packer shall not change the Formula, any Material, the supplier of any Material, the
process, the line, the Facility, any packaging component, the coding or the label version for
any Product without Brand's prior written approval. It shall give at least
[CHANGE NOTICE PERIOD] written notice of a proposed change, describing the change, the reason
for it, and its expected effect on the Product, the Specification, the cost and the lead time.
15.2 If a supply interruption makes a change unavoidable, Co-Packer shall notify Brand
immediately and shall not produce Product incorporating the change until Brand approves it in
writing. Brand may require a qualification run, retained samples, analytical results or
stability data before approving. An approved change takes effect only when the Specification is
amended, given a new version number and effective date, and signed by both parties.

16. RECALLS AND WITHDRAWALS
16.1 Brand has the sole right to decide whether to recall, withdraw, hold or otherwise remove
any Product from the market, and to set the scope. Co-Packer shall not make that decision for
Brand's Products and shall not delay or condition Brand's decision.
16.2 Each party shall notify the other immediately on becoming aware of information suggesting a
Product may be unsafe, adulterated, mislabelled or otherwise unfit for sale. Co-Packer shall
cooperate fully, including by delivering trace results within [TRACE RESPONSE TIME], producing
batch records, certificates of analysis and Materials records, holding and segregating affected
Product and retained samples, making personnel available, and assisting with the root cause
investigation. Brand controls all communications about the Products with retailers,
distributors, consumers and the public, and neither party shall make a public statement naming
the other in connection with a recall without the other's prior written consent, except where
required by law.
16.3 Recall costs include the cost of the affected Product; freight in both directions; retailer
and distributor chargebacks, handling and destruction fees; storage, sorting and inspection;
disposal; notification and customer communication; the fees of any third-party recall firm; the
cost of the investigation; and the cost of replacement Product. They are allocated according to
the cause established by the root cause investigation:

(a) Co-Packer bears them where the cause is its failure to meet the Specifications, its
    manufacturing, handling, storage, sanitation, allergen control or coding practices, or a
    Material Co-Packer procured;
(b) Brand bears them where the cause is the Formula, the label copy or artwork Brand supplied, a
    claim made by Brand, a Material Brand procured, or handling after Brand or its carrier took
    possession; and
(c) where more than one cause contributes, or the cause cannot be determined after a good faith
    investigation, the costs are shared in proportion to each party's contribution, and where no
    proportion can be established, equally.

16.4 Each party bears its own costs until the allocation is agreed, subject to reallocation, and
neither may withhold cooperation, records or Product pending it. The parties shall conduct a
mock recall together [MOCK RECALL FREQUENCY], and Co-Packer shall provide the written results.

17. INSURANCE
17.1 Co-Packer shall maintain, at its own cost, commercial general liability insurance of at
least [GENERAL LIABILITY LIMIT], product liability and products-completed operations coverage of
at least [PRODUCT LIABILITY LIMIT], and product recall or contaminated products coverage of at
least [RECALL COVERAGE LIMIT], together with workers compensation and property coverage
applicable to its operations. Recall coverage and product liability coverage are separate
covers, and neither satisfies the requirement for the other.
17.2 Co-Packer shall name Brand as an additional insured on its commercial general liability and
product liability policies with respect to the Products, provide certificates of insurance on
the Effective Date, on each renewal and on request, and give at least [INSURANCE NOTICE PERIOD]
written notice before a policy is cancelled, is not renewed or is materially reduced. Insurance
does not limit Co-Packer's obligations under Sections 16, 24 or 25.

18. CONFIDENTIALITY
18.1 Each party shall hold the other's confidential information in confidence, use it only to
perform this Agreement, and disclose it only to personnel and advisors who need it and who are
bound by obligations at least as protective as these. All Brand Property is Brand's confidential
information, and Co-Packer shall not use it for any product other than the Products, for any
other customer, or for itself. Co-Packer shall not analyse, deconstruct or reverse engineer any
Product, Material or sample to derive the Formula or the Specifications, or permit anyone else
to do so.
18.2 These obligations do not apply to information a receiving party can demonstrate was known
to it without a duty of confidentiality before disclosure, is or becomes public through no act
or omission of the receiving party, is rightfully received from a third party free of any duty
of confidentiality, or was independently developed without use of or reference to the disclosing
party's confidential information.
18.3 The obligations survive for [CONFIDENTIALITY YEARS] years after termination and, for
information that constitutes a trade secret, for as long as it remains a trade secret. There is
no residuals right: neither party may use the other's confidential information on the basis that
its personnel retained it in memory.

19. FORMULA AND INTELLECTUAL PROPERTY
19.1 As between the parties, Brand exclusively owns the Formula, the Specifications, its
trademarks, artwork and label files, and all data, test results and stability data generated in
connection with the Products. Nothing in this Agreement transfers any interest in any of them to
Co-Packer.
19.2 All improvements, modifications and derivatives of the Formula or the Specifications, and
all know-how specific to the Products, are Brand's property whenever and by whomever conceived
in connection with this Agreement. Co-Packer assigns them to Brand and shall execute any
document reasonably needed to record that ownership. Brand grants Co-Packer a limited,
non-exclusive, non-transferable, revocable licence to use Brand Property solely to produce the
Products for Brand during the term, which ends on termination.
19.3 Co-Packer's equipment, general manufacturing know-how, and processes it owned before the
Effective Date or develops independently of the Products remain Co-Packer's. Nothing in this
Section gives Co-Packer any right in the Formula, the Specifications or the Products, and
Co-Packer shall not incorporate its pre-existing property into a Product in a way that would
restrict Brand's ability to have the Product made elsewhere without disclosing that fact in
writing before production. Co-Packer shall not use the Formula or the Specifications, in whole
or in part, to develop, manufacture, formulate or supply any product for itself or any third
party.

20. TOOLING AND CHANGE PARTS
20.1 Tooling, change parts, moulds, dies, plates and fixtures paid for by Brand are Brand's
property. Co-Packer shall tag and record them as Brand's, keep them in good working order, use
them only for the Products, and not use them for any other customer. Co-Packer shall maintain
them in the ordinary course at its own cost, shall repair or replace them at its own cost if
lost or damaged in its possession other than through ordinary wear, and shall return them on
request and in any event under Section 22. Tooling identified in Exhibit C as Co-Packer's
remains Co-Packer's property.

21. TERM AND TERMINATION
21.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], then renews
automatically for successive periods of [RENEWAL TERM] unless either party gives written notice
of non-renewal at least [NON-RENEWAL NOTICE] before the end of the then-current term.
21.2 Either party may terminate on written notice if the other materially breaches and does not
cure within [CURE PERIOD] after written notice describing the breach, and may terminate
immediately if the other becomes insolvent, makes a general assignment for the benefit of
creditors, or has a receiver appointed over a substantial part of its assets. Brand may also
terminate if Co-Packer loses a certification required by Section 11 and does not regain it
within [CURE PERIOD], or on repeated failures to meet the Specifications or confirmed production
dates. Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE] written
notice.
21.3 On termination Co-Packer shall complete purchase orders accepted before the notice unless
Brand directs otherwise or the termination is for Co-Packer's breach. Brand shall pay undisputed
amounts for Product delivered and for Materials properly committed under Section 8, subject to
the cap in that Section.

22. TRANSITION ASSISTANCE
22.1 For [TRANSITION PERIOD] after termination or expiry, Co-Packer shall cooperate in good
faith to move production without disruption to Brand's supply. Within that period Co-Packer
shall:

(a) deliver all finished Products and work in progress to Brand or Brand's designee;
(b) deliver all Materials paid for by Brand, with their lot documentation and remaining shelf life;
(c) return all Brand tooling and change parts;
(d) return or destroy, at Brand's election, all labels and other components bearing Brand's
    trademarks, with written confirmation of any destruction;
(e) return all copies of the Formula, the Specifications, artwork and label files and any other
    documents containing Brand Property; and
(f) deliver batch records, certificates of analysis, reconciliations, trace records and retained
    samples for Lots produced under this Agreement.

22.2 Co-Packer shall also provide Brand and any manufacturer Brand nominates with the process
parameters, equipment settings, supplier lot information and production history needed to
reproduce the Products, at Brand's cost for Co-Packer's reasonable time; this does not require
Co-Packer to disclose general manufacturing know-how that is not specific to the Products.
Co-Packer shall not withhold Brand Property, finished Product or records under this Section on
account of a disputed amount, and shall not condition transition assistance on the settlement of
a dispute. Brand remains liable for undisputed amounts.

23. REPRESENTATIONS AND WARRANTIES
23.1 Each party represents that it is duly organised, has authority to enter into this
Agreement, and that doing so does not breach any other agreement binding on it. Co-Packer
warrants that each Product will conform to its Specification, will be produced at the Facility
using Materials meeting the Specification, will be free from defects in workmanship, and will be
produced under Co-Packer's food safety, sanitation and allergen programs; and that it has good
title, free of liens, to Product it supplies and Materials it procures. Brand warrants that it
has the right to provide the Formula, trademarks, artwork and label content it supplies. Except
as stated in this Agreement, neither party makes any other warranty, express or implied.

24. INDEMNIFICATION
24.1 Each party shall defend and indemnify the other and its officers, directors, employees and
agents against third-party claims, and the resulting losses, damages, costs and reasonable legal
fees, arising from its own breach of this Agreement, negligence or wilful misconduct. Without
limiting that, Co-Packer indemnifies Brand for claims arising from its failure to meet the
Specifications, its handling or storage of the Products, or Materials it procured; and Brand
indemnifies Co-Packer for claims arising from the Formula, the label copy or artwork Brand
supplied, claims made by Brand about the Products, or Materials Brand procured.
24.2 The indemnified party shall give prompt written notice of a claim, allow the indemnifying
party to control the defence with counsel it selects, and cooperate at the indemnifying party's
cost. The indemnifying party shall not settle in a way that imposes an obligation or an
admission on the indemnified party without its written consent.

25. LIMITATION OF LIABILITY
25.1 Neither party is liable to the other for indirect, incidental, special, punitive or
consequential damages or for lost profits, and each party's total liability arising out of this
Agreement is limited to [LIABILITY CAP].
25.2 That limitation does not apply to recall costs allocated under Section 16; a party's
indemnity obligations for third-party claims of bodily injury, death or damage to tangible
property; breach of Section 18; misappropriation or infringement of Brand Property or breach of
Section 19; a party's gross negligence or wilful misconduct; or amounts owed for Product
delivered.

26. GENERAL
26.1 The parties are independent contractors, and nothing creates a partnership, joint venture,
agency or employment relationship. Neither party may assign this Agreement without the other's
prior written consent, except to a successor in a merger or a sale of substantially all of its
assets, provided the successor is not a competitor of the other party in the [PRODUCT CATEGORY]
category and assumes this Agreement in writing.
26.2 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance; payment
obligations are not excused. If the event continues for more than [FORCE MAJEURE PERIOD], either
party may terminate on written notice, and Brand may source the affected Products elsewhere in
the meantime without breaching any commitment in this Agreement.
26.3 Notices must be in writing and are effective on delivery to the addresses above or to any
address a party designates in writing. Neither party shall use the other's name, marks or logo
in marketing materials, customer lists or public statements without the other's prior written
consent.
26.4 This Agreement, with its Exhibits, is the entire agreement between the parties on its
subject matter and supersedes all prior discussions. It may be amended only in a writing signed
by both parties. If any provision is held unenforceable, the remainder continues in effect, and
a failure to enforce a provision is not a waiver of it. It may be signed in counterparts,
including by electronic signature, each of which is an original. Sections 8, 13, 16, 18, 19, 20,
22, 24, 25 and 26 survive termination or expiry.
26.5 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without
regard to its conflict of laws principles. The parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CO-PACKER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


EXHIBIT A — PRODUCT SPECIFICATIONS AND TESTING PLAN. Attach the signed specification for each
Product, version [SPECIFICATION VERSION], with the testing plan required by Section 11.

EXHIBIT B — PRICING. Set out the tolling price for each Product, stated [PRICE UNIT], the cost
items that may be passed through under Section 6, any handling or storage charges, and the free
holding period for finished goods.

EXHIBIT C — TOOLING AND CHANGE PARTS. List Brand-owned tooling and change parts, and separately
any tooling that remains Co-Packer's property under Section 20.

EXHIBIT D — CERTIFICATIONS AND INSURANCE. Attach evidence of [FACILITY CERTIFICATIONS] and
current certificates of insurance.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 2. MANUFACTURING NDA
  // ───────────────────────────────────────────────────────────────────────────────────────
  // ───────────────────────────────────────────────────────────────────────────────────────
  // 3. QUALITY AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "quality-agreement",
    docId: "quality-agreement",
    title: "QUALITY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Alongside your co-packing or contract manufacturing agreement, and before a retailer, a distributor or an insurer asks how quality is controlled at the plant making your product.",
      whyItMatters:
        "Every quality dispute is the same argument: did the product meet an agreed standard, and who was supposed to check. Without this document that argument starts from nothing and is settled by whoever has more leverage, which is not you. It is also the document buyers and insurers ask for, and its absence tells them nobody has decided who is accountable for what is in the can.",
      negotiateCarefully: [
        {
          point: "Who releases product, and on what evidence",
          detail:
            "If the plant releases on its own judgement, product can ship before you have seen a single result. Decide deliberately whether you require positive release, and price the working capital it costs before you ask for it.",
        },
        {
          point: "Every test assigned to a named party",
          detail:
            "Where the plan is vague both sides assume the other is testing and nobody is. List each test with the method, the frequency, who performs it and who pays. This surfaces only when you need a result you do not have.",
        },
        {
          point: "Change notification thresholds",
          detail:
            "If only 'material' changes need notice and nobody defines material, ingredient supplier swaps and process changes happen silently. Make any change to formula, supplier, process, line or packaging component notifiable and approvable.",
        },
        {
          point: "Records retention against your shelf life",
          detail:
            "Batch records, environmental results and COAs are the evidence in a trace exercise. A retention period shorter than shelf life plus a margin destroys the evidence before the complaint arrives.",
        },
        {
          point: "Which document controls on a conflict",
          detail:
            "This agreement and the manufacturing agreement will overlap. Say plainly that this one governs technical and quality matters and the manufacturing agreement governs commercial ones, so a rejection does not become an argument about paperwork.",
        },
      ],
      redFlags: [
        "No named, versioned specification, or one referenced but never attached.",
        "Release authority resting entirely with the plant with no notice to you.",
        "No obligation to notify you of deviations, holds, complaints or certification changes.",
        "Records retention shorter than the product's shelf life.",
        "No audit right, and refusal to share third-party audit results.",
        "Change notification limited to changes the plant alone considers significant.",
      ],
      timeToComplete: "45 minutes to fill with your co-packer's QA lead",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[MANUFACTURER NAME]", label: "Manufacturer's legal name", example: "Midwest Co-Packing, Inc." },
      { token: "[MANUFACTURER ADDRESS]", label: "Manufacturer's address", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[FACILITY ADDRESS]", label: "Address of the production facility", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[MANUFACTURING AGREEMENT DATE]", label: "Date of the manufacturing agreement", example: "September 1, 2026" },
      { token: "[PRODUCT LIST]", label: "Products covered", example: "Acme Sparkling Yuzu, 12 oz can" },
      { token: "[SPECIFICATION VERSION]", label: "Specification version and date", example: "v1.2 dated August 12, 2026" },
      { token: "[BRAND QUALITY CONTACT]", label: "Your quality contact", example: "Operations Lead, ops@example.com" },
      { token: "[MANUFACTURER QUALITY CONTACT]", label: "Their quality contact", example: "QA Manager, qa@example.com" },
      { token: "[RELEASE AUTHORITY]", label: "Who releases each lot", example: "Manufacturer, subject to Brand's positive release for the first three lots" },
      { token: "[COA DELIVERY TIMING]", label: "When the COA is delivered", example: "before or with each shipment" },
      { token: "[RETAINED SAMPLE QUANTITY]", label: "Retained samples per lot", example: "12 units per lot" },
      { token: "[RETAINED SAMPLE PERIOD]", label: "How long retained samples are held", example: "shelf life plus 6 months" },
      { token: "[INSPECTION PERIOD]", label: "Your inspection and rejection window", example: "30 days after receipt" },
      { token: "[REJECTION RESPONSE PERIOD]", label: "Time to respond to a rejection", example: "10 business days" },
      { token: "[DEVIATION NOTICE TIME]", label: "Time to report a deviation", example: "1 business day" },
      { token: "[INVESTIGATION PERIOD]", label: "Time to complete an investigation", example: "15 business days" },
      { token: "[COMPLAINT NOTICE TIME]", label: "Time to pass on a complaint", example: "2 business days" },
      { token: "[CHANGE NOTICE PERIOD]", label: "Notice required before a change", example: "45 days" },
      { token: "[AUDIT FREQUENCY]", label: "How often you may audit", example: "once per calendar year" },
      { token: "[AUDIT NOTICE]", label: "Notice required before an audit", example: "10 business days" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long records are kept", example: "shelf life plus 2 years" },
      { token: "[TRACE RESPONSE TIME]", label: "Time to complete a lot trace", example: "4 hours" },
      { token: "[MOCK RECALL FREQUENCY]", label: "How often a mock recall is run", example: "once per calendar year" },
      { token: "[FACILITY CERTIFICATIONS]", label: "Certifications the facility must hold", example: "its third-party food safety certification and organic certification" },
      { token: "[REVIEW FREQUENCY]", label: "How often this agreement is reviewed", example: "annually" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Quality Agreement (this "Quality Agreement") is entered into as of [EFFECTIVE DATE]
between [COMPANY NAME], of [COMPANY ADDRESS] ("Brand"), and [MANUFACTURER NAME], of
[MANUFACTURER ADDRESS] ("Manufacturer").

It applies to the manufacture of the [BRAND NAME] products listed below at Manufacturer's
facility at [FACILITY ADDRESS] (the "Facility"), and supports the manufacturing agreement
between the parties dated [MANUFACTURING AGREEMENT DATE] (the "Manufacturing Agreement").

1. PRODUCTS AND SCOPE
1.1 This Quality Agreement covers: [PRODUCT LIST] (the "Products").
1.2 It allocates responsibility between the parties for specifications, testing, release,
rejection, deviations, complaints, change control, records, audits and traceability.

2. RELATIONSHIP TO THE MANUFACTURING AGREEMENT
2.1 This Quality Agreement governs the technical and quality matters it addresses. The
Manufacturing Agreement governs commercial matters, including price, forecasts, orders,
payment, materials liability, insurance, indemnity, limitation of liability, term and
termination.
2.2 If a term of this Quality Agreement conflicts with the Manufacturing Agreement on a
technical or quality matter, this Quality Agreement controls. On any other matter, including
liability and remedies, the Manufacturing Agreement controls.
2.3 Nothing in this Quality Agreement changes the allocation of liability, indemnity or cost
set out in the Manufacturing Agreement.

3. QUALITY CONTACTS
3.1 Brand's quality contact is [BRAND QUALITY CONTACT]. Manufacturer's quality contact is
[MANUFACTURER QUALITY CONTACT].
3.2 Notices required by this Quality Agreement are given to those contacts and are effective
on delivery. Each party shall notify the other promptly of a change of contact.

4. SPECIFICATIONS
4.1 Each Product is defined by its written specification, version [SPECIFICATION VERSION],
signed by both parties and attached as Schedule 1 (the "Specification").
4.2 The Specification is the sole standard for conformance. A Product conforms if it meets the
Specification; it does not conform if it does not.
4.3 The Specification may be amended only under Section 10, and each amendment takes a new
version number and effective date and is signed by both parties.
4.4 Manufacturer shall keep the current Specification available to the personnel producing the
Products and shall not produce to a superseded version.

5. SAMPLING AND TESTING
5.1 Schedule 2 sets out the testing plan. For each test it states the parameter, the
specification limit, the method, the sampling point, the frequency, the party performing the
test and the party bearing the cost.
5.2 A test not assigned in Schedule 2 is not being performed. Neither party may assume the
other is performing an unassigned test.
5.3 Manufacturer shall sample in accordance with the plan, using a sampling method that
represents the Lot, and shall retain the raw data supporting each result.
5.4 Manufacturer shall retain [RETAINED SAMPLE QUANTITY] of each Lot under the storage
conditions stated in the Specification for [RETAINED SAMPLE PERIOD], and shall provide
retained samples to Brand on request.
5.5 Either party may perform additional testing at its own cost. Where results conflict, the
parties shall submit samples from the Lot to an independent laboratory both approve, whose
result is final; the cost is borne by the party whose position is not supported.

6. CERTIFICATE OF ANALYSIS
6.1 Manufacturer shall issue a Certificate of Analysis for each Lot and deliver it to Brand
[COA DELIVERY TIMING].
6.2 Each Certificate of Analysis shall state: the Product name and code; the Lot code and
production date; the quantity produced; each parameter tested, with the specification limit,
the method used and the actual result obtained; the disposition of the Lot; and the name and
title of the person releasing it.
6.3 A Certificate of Analysis stating only that the Lot conforms, without the parameters,
methods and results, does not satisfy this Section.
6.4 Manufacturer shall issue a corrected Certificate of Analysis promptly on discovering an
error, and shall notify Brand of the correction.

7. RELEASE
7.1 Release authority for each Lot rests with [RELEASE AUTHORITY].
7.2 No Lot shall be shipped before the release conditions in Schedule 2 are met and the
Certificate of Analysis has been issued, unless Brand agrees otherwise in writing for a
specific Lot.
7.3 Manufacturer shall hold any Lot with an out-of-specification or atypical result and shall
notify Brand before taking any further action on it.

8. ACCEPTANCE, REJECTION AND DISPOSITION
8.1 Brand may inspect and test each Lot and may reject non-conforming Product within
[INSPECTION PERIOD], and at any time for a defect not reasonably discoverable on inspection.
8.2 Manufacturer shall respond to a rejection within [REJECTION RESPONSE PERIOD], stating
whether it accepts the rejection and, if not, on what evidence.
8.3 Non-conforming Product shall be quarantined, identified and physically segregated pending
disposition.
8.4 Non-conforming Product shall not be reworked, reprocessed, blended, downgraded, sold,
donated or otherwise released without Brand's prior written approval of the specific proposed
disposition. Where rework is approved, the reworked Lot is tested against the Specification
before release and is identified as reworked in the batch record.
8.5 Product bearing Brand's trademarks shall not leave the Facility except to Brand or at
Brand's written direction. Where Product is destroyed, Manufacturer shall provide written
confirmation identifying the Lot, the quantity and the method of destruction.

9. DEVIATIONS, INVESTIGATIONS AND COMPLAINTS
9.1 Manufacturer shall notify Brand of any deviation from the Specification, the process or
its own procedures affecting the Products, and of any hold or out-of-specification result,
within [DEVIATION NOTICE TIME] of becoming aware of it.
9.2 Manufacturer shall investigate each deviation, determine root cause where it can be
determined, define corrective and preventive actions, and deliver a written investigation
report to Brand within [INVESTIGATION PERIOD].
9.3 Each party shall pass on to the other any consumer, customer, retailer or distributor
complaint concerning the Products within [COMPLAINT NOTICE TIME] of receiving it, with the
information needed to investigate it, including the Lot code where available.
9.4 Manufacturer shall investigate complaints that concern manufacture, packaging, coding or
handling at the Facility and shall report the outcome to Brand. Brand shall handle
communication with the complainant unless the parties agree otherwise.
9.5 Manufacturer shall notify Brand promptly of any regulatory inspection finding, third-party
audit finding or certification change that affects or may affect the Products.

10. CHANGE CONTROL
10.1 Manufacturer shall not change any of the following for the Products without Brand's prior
written approval: the formula; any raw material or packaging component; the supplier of any
raw material or packaging component; the process, equipment or line; the Facility; the coding
or date format; the label version; or the analytical method used for a specification
parameter.
10.2 Manufacturer shall give Brand at least [CHANGE NOTICE PERIOD] written notice of a
proposed change, describing the change, the reason, and its expected effect on the Product,
the Specification and the testing plan.
10.3 Where a supply interruption makes a change unavoidable, Manufacturer shall notify Brand
immediately and shall not produce Product incorporating the change until Brand has approved it
in writing.
10.4 Brand may require a qualification run, retained samples, analytical results or stability
data before approving a change.
10.5 An approved change takes effect only when the Specification and, where relevant, the
testing plan are amended, versioned, dated and signed by both parties.

11. FOOD SAFETY, SANITATION AND ALLERGEN CONTROL
11.1 Manufacturer is responsible for the Facility and for its own written food safety,
sanitation, pest control, environmental monitoring, water, maintenance and training programs,
and for its compliance with all laws applicable to it as manufacturer.
11.2 Brand is responsible for the formula, the label copy and artwork it supplies, and for the
accuracy of the claims made on the label.
11.3 Manufacturer shall disclose in writing the allergens handled at the Facility and on the
lines used for the Products, shall maintain documented and validated changeover and cleaning
procedures between products containing different allergens, and shall retain changeover
records.
11.4 Manufacturer shall not introduce a new allergen onto a line used for the Products without
notice and approval under Section 10.
11.5 Manufacturer shall maintain [FACILITY CERTIFICATIONS] throughout the term and shall
notify Brand promptly if any lapses, is suspended, is downgraded or is placed under review.

12. TRACEABILITY AND LOT CODING
12.1 Manufacturer shall apply a lot code to every unit, case and pallet of Product, in the
format stated in the Specification, that identifies the production date and the production
run.
12.2 Manufacturer shall maintain records that allow each finished pallet to be traced back to
every raw material and packaging lot consumed, and forward to the shipments in which it left
the Facility.
12.3 On Brand's request, Manufacturer shall complete a full forward and backward trace for a
Lot and deliver the results within [TRACE RESPONSE TIME].
12.4 The parties shall conduct a mock recall exercise together [MOCK RECALL FREQUENCY], and
Manufacturer shall provide the written results, including the percentage of product accounted
for and the time taken.

13. DOCUMENTS AND RECORDS
13.1 Manufacturer shall retain batch records, raw material and packaging receiving records and
lot traces, sampling and test data, Certificates of Analysis, sanitation and allergen
changeover records, deviation and investigation reports, complaint records, training records
and shipping records for the Products for [RECORD RETENTION PERIOD].
13.2 Manufacturer shall provide copies of any of those records to Brand on request and shall
not destroy records relating to a Lot that is the subject of an open complaint, investigation,
recall or dispute.
13.3 Records shall be legible, attributable, contemporaneous and retrievable by Lot code.

14. AUDITS
14.1 Brand and its representatives may audit the Facility, the records and the systems covered
by this Quality Agreement [AUDIT FREQUENCY] on [AUDIT NOTICE] written notice during normal
business hours.
14.2 Brand may audit on shorter notice following a Lot rejection, a deviation, a complaint
trend, a recall or a certification change.
14.3 Manufacturer shall provide Brand, on request, with the results of third-party audits of
the Facility and with any resulting corrective action plans.
14.4 Manufacturer shall respond in writing to audit observations within a period the parties
agree at the closing meeting, with corrective actions and completion dates.

15. RECALL COOPERATION
15.1 Brand decides whether to recall or withdraw its own Products. Manufacturer shall not make
that decision for Brand and shall not delay it.
15.2 Manufacturer shall cooperate fully in any recall or withdrawal, including by providing
trace results within [TRACE RESPONSE TIME], producing records, making personnel available and
assisting with the root cause investigation.
15.3 Allocation of recall costs is governed by the Manufacturing Agreement or by a separate
recall responsibility agreement between the parties, and not by this Quality Agreement.

16. REVIEW, TERM AND GENERAL
16.1 The parties shall review this Quality Agreement [REVIEW FREQUENCY] and on any material
change to the Products, the Facility or the process.
16.2 This Quality Agreement takes effect on the Effective Date and continues for as long as
Manufacturer produces the Products, and thereafter for as long as the record retention
obligations in Section 13 continue.
16.3 It may be amended only in a writing signed by both parties. It is governed by the laws of
the State of [GOVERNING LAW STATE], without regard to its conflict of laws principles, and the
parties consent to venue in [VENUE]. It may be signed in counterparts, including by electronic
signature.

AGREED AND ACCEPTED.


[COMPANY NAME]                            [MANUFACTURER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


SCHEDULE 1 — SPECIFICATIONS
Attach the signed specification for each Product, version [SPECIFICATION VERSION].

SCHEDULE 2 — TESTING AND RELEASE PLAN
For each parameter, state the specification limit, the method, the sampling point, the
frequency, the party performing the test, the party bearing the cost, and whether the result
is required before release.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 4. PRODUCT SPECIFICATION SHEET
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "product-specification-sheet",
    docId: "product-specification-sheet",
    title: "FINISHED PRODUCT SPECIFICATION SHEET",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before your first commercial run, and attached to every manufacturing, quality and supply agreement you sign. Distributors and retailers will also ask for it during onboarding.",
      whyItMatters:
        "This is the document that defines the phrase 'correct product'. Every argument about a bad run, every rejection, every credit and every recall decision is measured against it. Founders who never signed one find that a run which is visibly wrong is still contractually fine, because nothing said what right was. It is the cheapest document in the category to produce and the one most often missing.",
      negotiateCarefully: [
        {
          point: "A tolerance for every target",
          detail:
            "An attribute with a target and no range cannot be enforced, and a range copied from the plant's default may accept product your customers will notice is different. Set the ranges against your own quality expectations, then check the plant can hold them.",
        },
        {
          point: "Packaging by supplier part number",
          detail:
            "Describing the can, closure, label or carton generically lets a substitute component appear that does not run on the line or does not match the artwork. Part numbers connect this sheet to the packaging specification.",
        },
        {
          point: "Shelf life tied to actual data",
          detail:
            "State the storage conditions the dating assumes and the study that supports it. A number chosen because it looked right on the pack is one you cannot defend to a retailer who asks for the study.",
        },
        {
          point: "What a Certificate of Analysis must report",
          detail:
            "List the parameters and methods here. If the sheet does not say what a COA must contain, you receive a document that confirms nothing you can act on.",
        },
        {
          point: "Version, date and both signatures",
          detail:
            "An unversioned sheet makes it impossible to say which standard applied to a lot produced six months ago, and an unsigned one is a proposal rather than a standard.",
        },
      ],
      redFlags: [
        "A specification the manufacturer will not sign.",
        "Attributes listed with targets but no acceptable ranges.",
        "Shelf life stated with no supporting stability data or storage conditions.",
        "No allergen or cross-contact statement.",
        "No version number or effective date.",
      ],
      timeToComplete: "2–3 hours with your formulator and co-packer · re-sign on every change",
    },

    placeholders: [
      { token: "[SPECIFICATION VERSION]", label: "Version number", example: "v1.2" },
      { token: "[EFFECTIVE DATE]", label: "Effective date of this version", example: "September 1, 2026" },
      { token: "[SUPERSEDES VERSION]", label: "Version this replaces", example: "v1.1 dated May 4, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[BRAND NAME]", label: "Brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[PRODUCT NAME]", label: "Product name as labelled", example: "Acme Sparkling Yuzu" },
      { token: "[SKU CODE]", label: "Internal SKU or item code", example: "ACM-YUZ-12" },
      { token: "[UNIT SIZE]", label: "Unit size and format", example: "12 fl oz slim can" },
      { token: "[MANUFACTURER NAME]", label: "Manufacturer's legal name", example: "Midwest Co-Packing, Inc." },
      { token: "[FACILITY ADDRESS]", label: "Production facility", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[FORMULA REFERENCE]", label: "Batch formula document and version", example: "Formula ACM-YUZ rev 4" },
      { token: "[INGREDIENT STATEMENT REFERENCE]", label: "Where the ingredient statement lives", example: "Label artwork ACM-YUZ-12 rev 7, panel 2" },
      { token: "[LABEL ARTWORK VERSION]", label: "Approved artwork version", example: "rev 7 dated July 3, 2026" },
      { token: "[APPEARANCE STANDARD]", label: "Appearance description", example: "clear pale yellow liquid, no sediment" },
      { token: "[FLAVOR STANDARD]", label: "Sensory reference standard", example: "matches retained gold standard sample GS-12" },
      { token: "[ANALYTICAL PARAMETERS]", label: "Analytical parameters with target and tolerance", example: "pH 3.2 (3.0–3.4); Brix 9.5 (9.0–10.0); CO2 2.6 vol (2.4–2.8)" },
      { token: "[MICRO PARAMETERS]", label: "Microbiological parameters and limits", example: "as listed in the attached micro schedule" },
      { token: "[NET CONTENTS]", label: "Declared net contents", example: "12 fl oz (355 mL)" },
      { token: "[FILL TARGET AND TOLERANCE]", label: "Fill target and acceptable range", example: "357 mL target, 353–362 mL" },
      { token: "[PACKAGING COMPONENTS]", label: "Packaging components by supplier part number", example: "Can: supplier part 12SLM-001; End: 202 LOE part E202-14; Label: part LBL-YUZ-7" },
      { token: "[CASE CONFIGURATION]", label: "Case pack", example: "12 cans per case, printed shipper" },
      { token: "[PALLET CONFIGURATION]", label: "Pallet configuration", example: "10 cases per layer, 8 layers, 80 cases, stretch wrapped" },
      { token: "[PALLET TYPE]", label: "Pallet type", example: "48x40 four-way heat-treated wood" },
      { token: "[CODE FORMAT]", label: "Lot and date code format", example: "LLLYYDDD followed by line and time" },
      { token: "[CODE LOCATION]", label: "Where the code is applied", example: "can bottom, case end panel, pallet placard" },
      { token: "[DATE FORMAT]", label: "Consumer date format and phrase", example: "BEST BY MMM DD YYYY" },
      { token: "[SHELF LIFE]", label: "Shelf life", example: "12 months from production" },
      { token: "[STORAGE CONDITIONS]", label: "Storage conditions the shelf life assumes", example: "ambient, dry, out of direct sunlight" },
      { token: "[STABILITY REFERENCE]", label: "Study supporting the shelf life", example: "Stability study ACM-YUZ-ST-02 dated March 2026" },
      { token: "[MINIMUM SHELF LIFE ON DELIVERY]", label: "Minimum remaining shelf life at delivery", example: "at least 85% of total shelf life" },
      { token: "[ALLERGEN STATEMENT]", label: "Allergens present in the formula", example: "none intentionally present" },
      { token: "[CROSS CONTACT STATEMENT]", label: "Allergens handled in the facility or on the line", example: "milk and tree nuts are handled in the facility, not on this line" },
      { token: "[COA PARAMETERS]", label: "Parameters required on every COA", example: "pH, Brix, CO2 volumes, net fill, micro panel" },
      { token: "[TESTING FREQUENCY]", label: "Testing frequency", example: "every lot for analytical, every lot for micro" },
      { token: "[RETAINED SAMPLE QUANTITY]", label: "Retained samples per lot", example: "12 units" },
      { token: "[RETAINED SAMPLE PERIOD]", label: "Retention period for samples", example: "shelf life plus 6 months" },
      { token: "[REJECTION CRITERIA]", label: "What makes a lot rejectable", example: "any analytical result outside range, any micro failure, illegible coding" },
      { token: "[BRAND APPROVER]", label: "Your approver's name and title", example: "Jordan Lee, Operations Lead" },
      { token: "[MANUFACTURER APPROVER]", label: "Their approver's name and title", example: "QA Manager" },
    ],

    body: `FINISHED PRODUCT SPECIFICATION

Version: [SPECIFICATION VERSION]      Effective date: [EFFECTIVE DATE]
Supersedes: [SUPERSEDES VERSION]

This specification is issued by [COMPANY NAME] ("Brand") for manufacture by
[MANUFACTURER NAME] ("Manufacturer") at [FACILITY ADDRESS]. It defines the Product and is the
standard against which conformance, acceptance and rejection are measured. It is not effective
until signed by both parties.

SECTION 1 — PRODUCT IDENTITY
1.1 Product name as labelled: [PRODUCT NAME]
1.2 Brand: [BRAND NAME]
1.3 Category: [PRODUCT CATEGORY]
1.4 SKU or item code: [SKU CODE]
1.5 Unit size and format: [UNIT SIZE]
1.6 Manufacturing site: [FACILITY ADDRESS]

SECTION 2 — FORMULATION AND LABEL REFERENCE
2.1 Batch formula reference: [FORMULA REFERENCE]
    The batch formula is a separate controlled document owned by Brand. It is referenced here
    and is not reproduced in this specification.
2.2 Ingredient statement reference: [INGREDIENT STATEMENT REFERENCE]
    The declared ingredient statement on the approved artwork is the controlling statement.
    Any change to the formula that changes the ingredient statement requires a new artwork
    version and a new version of this specification.
2.3 Approved label artwork version: [LABEL ARTWORK VERSION]
2.4 Responsibility. Brand is responsible for the formula and for the content and accuracy of
    the label copy and claims. Manufacturer is responsible for producing to the formula and
    for applying the approved artwork version.

SECTION 3 — PHYSICAL AND SENSORY ATTRIBUTES
3.1 Appearance: [APPEARANCE STANDARD]
3.2 Flavour and aroma: [FLAVOR STANDARD]
3.3 Sensory method. Evaluation is against the retained reference standard identified above,
    assessed at the storage temperature stated in Section 8. Where the parties disagree, a
    panel agreed by both parties evaluates coded samples from the Lot and from the reference.

SECTION 4 — ANALYTICAL PARAMETERS
4.1 Each analytical parameter, with its target and its acceptable range:
    [ANALYTICAL PARAMETERS]
4.2 A result inside the stated range conforms. A result outside it does not conform, whether
    or not the Lot is otherwise acceptable.
4.3 A parameter listed with a target but no range is incomplete and is not enforceable; every
    parameter in this Section carries a range.

SECTION 5 — MICROBIOLOGICAL PARAMETERS
5.1 Microbiological parameters, limits and methods: [MICRO PARAMETERS]
5.2 Methods are stated with each parameter, because two laboratories using different methods
    can return different answers about the same Lot.

SECTION 6 — NET CONTENTS AND FILL
6.1 Declared net contents: [NET CONTENTS]
6.2 Fill target and acceptable range: [FILL TARGET AND TOLERANCE]
6.3 Manufacturer shall record fill checks at the frequency stated in Section 11 and retain the
    records with the batch record.

SECTION 7 — PACKAGING SPECIFICATION
7.1 Packaging components, by supplier and part number: [PACKAGING COMPONENTS]
7.2 No component may be substituted without Brand's prior written approval and a new version
    of this specification.
7.3 Case configuration: [CASE CONFIGURATION]
7.4 Pallet configuration: [PALLET CONFIGURATION]
7.5 Pallet type: [PALLET TYPE]
7.6 Pallets are stretch wrapped and placarded with the product name, SKU, lot code, quantity
    and production date.

SECTION 8 — CODING, DATING, SHELF LIFE AND STORAGE
8.1 Lot code format: [CODE FORMAT]
8.2 Code location: [CODE LOCATION]
8.3 Consumer date format and phrase: [DATE FORMAT]
8.4 Shelf life: [SHELF LIFE]
8.5 Storage conditions the shelf life assumes: [STORAGE CONDITIONS]
8.6 Supporting stability data: [STABILITY REFERENCE]
8.7 Minimum remaining shelf life on delivery to Brand or Brand's designee:
    [MINIMUM SHELF LIFE ON DELIVERY]
8.8 The lot code must permit the Lot to be traced back to every ingredient and packaging lot
    consumed and forward to the shipments in which it left the facility.

SECTION 9 — ALLERGEN STATEMENT
9.1 Allergens intentionally present in the formula: [ALLERGEN STATEMENT]
9.2 Allergens handled in the facility or on the line used for this Product:
    [CROSS CONTACT STATEMENT]
9.3 The statement in 9.2 describes the facility as operated on the effective date above. Any
    change to it requires notice to Brand and a new version of this specification before
    affected Product is produced.
9.4 Manufacturer maintains documented and validated allergen changeover procedures and retains
    the changeover records with the batch record.

SECTION 10 — TESTING AND CERTIFICATE OF ANALYSIS
10.1 Parameters required on every Certificate of Analysis: [COA PARAMETERS]
10.2 Each Certificate of Analysis states the Product, the lot code, the production date, the
     quantity, each parameter with its limit, the method used and the actual result, and the
     name of the person releasing the Lot.
10.3 Testing frequency: [TESTING FREQUENCY]
10.4 Retained samples: [RETAINED SAMPLE QUANTITY] per Lot, held under the storage conditions
     in Section 8 for [RETAINED SAMPLE PERIOD].

SECTION 11 — ACCEPTANCE AND REJECTION
11.1 A Lot conforms when it meets every parameter in Sections 3 through 9 and is accompanied
     by a complete Certificate of Analysis.
11.2 Rejection criteria: [REJECTION CRITERIA]
11.3 Non-conforming Product is quarantined and segregated, and is not reworked, blended,
     downgraded, sold, donated or released without Brand's prior written approval of the
     specific disposition.
11.4 This specification controls on any question of what constitutes conforming Product. Where
     it conflicts with a manufacturing or supply agreement on conformance, this specification
     controls; that agreement continues to govern commercial matters and remedies.

SECTION 12 — CHANGE CONTROL
12.1 No change to the formula, an ingredient, a supplier, the process, the line, a packaging
     component, the coding or the artwork may be made without Brand's prior written approval.
12.2 An approved change takes effect only when this specification is reissued with a new
     version number and effective date and is signed by both parties.
12.3 The superseded version remains on file and continues to describe Lots produced while it
     was in effect.

SECTION 13 — APPROVAL
This specification is approved as the standard for [PRODUCT NAME], [SKU CODE], effective
[EFFECTIVE DATE].


[COMPANY NAME]                            [MANUFACTURER NAME]

Approver: [BRAND APPROVER]                Approver: [MANUFACTURER APPROVER]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 5. RECALL RESPONSIBILITY AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "recall-responsibility-agreement",
    docId: "recall-responsibility-agreement",
    title: "RECALL RESPONSIBILITY AND COST ALLOCATION AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before the first commercial production run, and again whenever you add a manufacturer, a co-manufacturer or a significant ingredient supplier.",
      whyItMatters:
        "A recall is the fastest way a small brand becomes insolvent, and almost none of the cost is the product itself. It is freight both ways, retailer chargebacks and handling fees, disposal, notification, a third-party recall firm, lost sales, and the cost of getting back onto a shelf you were removed from. When the agreement is silent, all of that lands on the brand by default while the parties argue about root cause. Deciding it while everyone is calm is one of the highest-value hours a founder can spend.",
      negotiateCarefully: [
        {
          point: "You decide, alone, whether to pull product",
          detail:
            "If the manufacturer has to agree before product comes off the shelf, you can be blocked from acting on your own brand while the risk keeps shipping. Keep the decision unilateral and make cooperation mandatory regardless of who is eventually found at fault.",
        },
        {
          point: "Name every cost category",
          detail:
            "A clause covering 'the value of the affected product' leaves out most of the real bill. List freight both ways, retailer and distributor chargebacks and handling fees, disposal, notification, the recall firm, the investigation and replacement product.",
        },
        {
          point: "Carve recall out of the liability cap",
          detail:
            "A cap set at the value of a production run makes the allocation meaningless in exactly the event it was written for. Carve recall costs and third-party injury claims out of both the cap and the exclusion of consequential damages.",
        },
        {
          point: "Recall cover is not product liability cover",
          detail:
            "They are different policies covering different costs. Require both by name, with limits, certificates and additional insured status, and check what your own policy actually supports before you promise a limit.",
        },
        {
          point: "Trace speed decides recall size",
          detail:
            "The width of a recall is set by how precisely and how quickly you can identify affected lots. Put a response time on the trace, require lot-level linkage from ingredient receipt to finished pallet, and run a mock exercise to prove it works.",
        },
      ],
      redFlags: [
        "Recovery limited to the invoice value of the affected product.",
        "A general liability cap that also caps recall and bodily injury claims.",
        "The manufacturer holding a veto over your decision to recall or withdraw.",
        "No insurance requirement, or no evidence of recall-specific cover.",
        "No traceability commitment and no stated trace response time.",
        "Cost allocation that depends on proving cause with evidence the other party controls.",
      ],
      timeToComplete: "45 minutes to fill · negotiate alongside the manufacturing agreement",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[MANUFACTURER NAME]", label: "Manufacturer's legal name", example: "Midwest Co-Packing, Inc." },
      { token: "[MANUFACTURER STATE]", label: "Manufacturer's state of formation", example: "Ohio" },
      { token: "[MANUFACTURER ENTITY TYPE]", label: "Manufacturer's entity type", example: "corporation" },
      { token: "[MANUFACTURER ADDRESS]", label: "Manufacturer's address", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[FACILITY ADDRESS]", label: "Production facility", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[PRODUCT LIST]", label: "Products covered", example: "all Acme products produced at the Facility" },
      { token: "[NOTIFICATION TIME]", label: "Time to notify the other party of a potential issue", example: "immediately and in any event within 4 hours" },
      { token: "[DECISION MEETING TIME]", label: "Time to convene a decision call", example: "within 12 hours of notification" },
      { token: "[BRAND RECALL CONTACT]", label: "Your recall contact", example: "Founder / Operations Lead, mobile and email" },
      { token: "[MANUFACTURER RECALL CONTACT]", label: "Their recall contact", example: "QA Manager, mobile and email" },
      { token: "[TRACE RESPONSE TIME]", label: "Time to complete a lot trace", example: "4 hours" },
      { token: "[RECORDS RESPONSE TIME]", label: "Time to produce records on request", example: "1 business day" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long records are kept", example: "shelf life plus 2 years" },
      { token: "[INVESTIGATION PERIOD]", label: "Time to complete the root cause investigation", example: "20 business days" },
      { token: "[COST CLAIM PERIOD]", label: "Time to submit documented recall costs", example: "90 days after the recall is closed" },
      { token: "[COST PAYMENT PERIOD]", label: "Time to pay an allocated cost claim", example: "30 days after the allocation is agreed" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$2,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$5,000,000 aggregate" },
      { token: "[RECALL COVERAGE LIMIT]", label: "Recall / contaminated products limit", example: "$1,000,000" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[MOCK RECALL FREQUENCY]", label: "How often a mock recall is run", example: "once per calendar year" },
      { token: "[MOCK RECALL TARGET]", label: "Mock recall performance target", example: "99% of product accounted for within 4 hours" },
      { token: "[TERM]", label: "Term of this agreement", example: "for as long as Manufacturer produces the Products" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Recall Responsibility and Cost Allocation Agreement (this "Agreement") is entered into
as of [EFFECTIVE DATE] by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], of [COMPANY ADDRESS] ("Brand");
  and

  [MANUFACTURER NAME], a [MANUFACTURER STATE] [MANUFACTURER ENTITY TYPE], of
  [MANUFACTURER ADDRESS] ("Manufacturer").

It applies to the [BRAND NAME] products produced by Manufacturer at [FACILITY ADDRESS] (the
"Facility"), being [PRODUCT LIST] (the "Products").

1. PURPOSE AND DEFINITIONS
1.1 The parties are agreeing in advance who decides, who leads, who cooperates and who pays,
so that those questions are settled before an incident rather than during one.
1.2 "Recall" means the removal of Product from the market or from consumers. "Withdrawal"
means the removal of Product from distribution before it reaches consumers. "Recall Event"
means either, and includes a market hold, a stock recovery and a public advisory about the
Products.
1.3 "Affected Product" means Product identified by lot code as within the scope of a Recall
Event.

2. DECISION AUTHORITY
2.1 Brand has the sole and exclusive right to decide whether to commence a Recall Event for
the Products, its scope, and when it ends.
2.2 Manufacturer shall not commence a Recall Event for the Products, shall not communicate a
Recall Event to Brand's customers, and shall not delay or condition Brand's decision.
2.3 Manufacturer retains its own obligations in respect of its facility and its other
customers, and nothing in this Agreement prevents Manufacturer from meeting them. Where
Manufacturer takes action affecting other products made on the same line, it shall notify
Brand and provide the information Brand needs to assess whether the Products are affected.
2.4 Brand's decision to act, or not to act, does not determine responsibility for cost.
Nothing in this Section affects the allocation in Section 7.

3. NOTIFICATION TRIGGERS AND TIMING
3.1 Each party shall notify the other within [NOTIFICATION TIME] of becoming aware of any of
the following in relation to the Products:
(a) a result, deviation, complaint or observation suggesting the Product may be unsafe,
    adulterated, mislabelled or otherwise unfit for sale;
(b) a confirmed out-of-specification result on a released Lot;
(c) an ingredient or packaging supplier notice affecting a lot used in the Products;
(d) a regulatory inspection finding, a customer or retailer inquiry, or a media or consumer
    report concerning the Products;
(e) a foreign material, allergen, labelling or coding error affecting a released Lot; or
(f) a Recall Event affecting another product made on the same line or with the same
    ingredient lot.
3.2 Notification is given to [BRAND RECALL CONTACT] and to
[MANUFACTURER RECALL CONTACT], by telephone and in writing. Each party shall keep those
details current and shall ensure the contact is reachable outside business hours.
3.3 The parties shall convene a joint call [DECISION MEETING TIME] to review the available
information. Notification is not an admission of responsibility by either party.

4. WHO LEADS
4.1 Brand leads the Recall Event. Brand determines scope, prepares and issues all notices to
distributors, retailers and consumers, manages returns and disposition, and manages
communication with any authority in relation to the Products.
4.2 Manufacturer leads the investigation into what happened at the Facility, provides
technical support, and manages the recovery of Product still in its possession.
4.3 Each party shall designate a single decision-maker for the duration of the Recall Event.

5. COOPERATION DUTIES
5.1 Manufacturer shall, on Brand's request and at its own cost during the Recall Event:
(a) complete a full forward and backward trace of the Affected Product and deliver the results
    within [TRACE RESPONSE TIME];
(b) produce batch records, Certificates of Analysis, raw material and packaging receiving
    records and lot traces, sanitation and allergen changeover records, deviation reports and
    shipping records within [RECORDS RESPONSE TIME];
(c) hold and segregate all Affected Product, work in progress and retained samples in its
    possession, and take no action to release, rework or destroy them without Brand's written
    direction;
(d) make its quality and production personnel available to Brand and to Brand's advisors; and
(e) preserve all records and physical evidence relating to the Affected Product until the
    parties agree in writing that they may be released.
5.2 Brand shall provide Manufacturer with the information it needs to conduct its
investigation, including complaint details, distribution data for the Affected Product and
results of any testing Brand performs.
5.3 Neither party shall make a public statement naming the other in connection with a Recall
Event without the other's prior written consent, except where required by law. Brand controls
all communications about the Products with distributors, retailers, consumers and the media.

6. RECORDS, TRACEABILITY AND MOCK RECALLS
6.1 Manufacturer shall apply lot coding that allows each finished pallet to be traced back to
every ingredient and packaging lot consumed and forward to the shipments in which it left the
Facility.
6.2 Manufacturer shall retain the records listed in Section 5.1(b) for
[RECORD RETENTION PERIOD], and shall not destroy records relating to Product that is the
subject of an open complaint, investigation, Recall Event or dispute.
6.3 The parties shall conduct a mock recall together [MOCK RECALL FREQUENCY], targeting
[MOCK RECALL TARGET]. Manufacturer shall provide the written results, including the quantity
accounted for and the time taken, and shall correct any gap the exercise reveals.
6.4 Brand shall maintain its own distribution records so that the Affected Product can be
traced from the Facility to each customer.

7. ROOT CAUSE AND COST ALLOCATION
7.1 Cost categories. Recall costs are:
(a) the cost of the Affected Product, valued at Brand's cost of goods for Product Brand owns
    and at the invoiced price for Product already sold;
(b) freight in both directions, including expedited freight and freight on replacement
    Product;
(c) retailer and distributor chargebacks, handling fees, restocking fees and administrative
    fees;
(d) storage, segregation, sorting and inspection costs;
(e) disposal and destruction costs;
(f) notification and customer communication costs, including press and consumer notices and
    call handling;
(g) the fees of any third-party recall, logistics or communications firm;
(h) the cost of the root cause investigation, including third-party laboratory testing; and
(i) the cost of producing replacement Product.
7.2 Investigation. The parties shall jointly determine root cause, each giving the other the
access and records it needs, and shall complete the investigation within
[INVESTIGATION PERIOD] where the cause can be determined in that time.
7.3 Allocation by cause. Recall costs are allocated as follows:
(a) Manufacturer bears them where the cause is Manufacturer's failure to meet the
    specification, its manufacturing, handling, storage, sanitation, allergen control or
    coding practices, its failure to follow an approved change, or an ingredient or packaging
    material Manufacturer procured;
(b) Brand bears them where the cause is the formula, the label copy or artwork Brand supplied,
    a claim made by Brand, an ingredient or packaging material Brand procured, or handling
    after Brand or its carrier took possession;
(c) where a third party such as an ingredient supplier is the cause, the party that selected
    and contracted with that third party bears the costs in the first instance, and shall
    pursue recovery from that third party and share any recovery with the other party in
    proportion to the costs each bore; and
(d) where more than one cause contributes, or where the cause cannot be determined after a
    good faith investigation, the costs are shared in proportion to each party's contribution
    to the cause, and where no proportion can be established, equally.
7.4 Interim position. Each party bears its own costs until the allocation is agreed or
determined, and pays them as they fall due, subject to reallocation. Neither party may
withhold cooperation, records or Product pending the allocation.
7.5 Claims. A party seeking reimbursement shall submit an itemised claim with supporting
documentation within [COST CLAIM PERIOD]. The other party shall pay the allocated amount
within [COST PAYMENT PERIOD] or state in writing which items it disputes and why.
7.6 Relationship to other agreements. This Section governs the allocation of recall costs
between the parties. Nothing in a limitation of liability, exclusion of consequential damages
or damages cap in any other agreement between the parties limits an obligation under this
Section, and the parties shall carve recall costs and third-party claims for bodily injury,
death or damage to tangible property out of any such limitation.

8. INSURANCE
8.1 Manufacturer shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT], product liability and products-completed operations coverage of at
least [PRODUCT LIABILITY LIMIT], and product recall or contaminated products coverage of at
least [RECALL COVERAGE LIMIT].
8.2 Brand shall maintain product liability coverage of at least [PRODUCT LIABILITY LIMIT] and
product recall coverage of at least [RECALL COVERAGE LIMIT].
8.3 Product recall coverage and product liability coverage are separate covers addressing
different costs. Neither party may rely on one to satisfy the requirement for the other.
8.4 Each party shall name the other as an additional insured on its commercial general
liability and product liability policies with respect to the Products, shall provide
certificates of insurance on the Effective Date, on each renewal and on request, and shall
give at least [INSURANCE NOTICE PERIOD] written notice before a policy is cancelled, is not
renewed or is materially reduced.
8.5 Each party shall notify its insurers of a Recall Event promptly and shall cooperate in the
presentation of claims. Insurance proceeds actually received for a cost category in Section
7.1 reduce the amount recoverable from the other party for that category.
8.6 The existence or limits of insurance do not limit either party's obligations under this
Agreement.

9. TERM, TERMINATION AND GENERAL
9.1 This Agreement takes effect on the Effective Date and continues [TERM], and thereafter for
as long as any Product produced during that period remains within its shelf life plus the
record retention period in Section 6.2.
9.2 Sections 5, 6, 7 and 8 survive termination in respect of Product produced before
termination.
9.3 This Agreement supplements any manufacturing, co-packing or quality agreement between the
parties. On any question of recall decision authority, cooperation or cost allocation, this
Agreement controls.
9.4 Neither party may assign this Agreement without the other's prior written consent, except
to a successor in a merger or sale of substantially all assets that assumes it in writing.
9.5 This Agreement may be amended only in a writing signed by both parties. If any provision
is held unenforceable, the remainder continues in effect.
9.6 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without
regard to its conflict of laws principles, and the parties consent to venue in [VENUE].
9.7 This Agreement may be signed in counterparts, including by electronic signature, each of
which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [MANUFACTURER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },
];

export default MANUFACTURING_TEMPLATES;
